import { action, makeObservable, observable, runInAction } from "mobx";
import {
  PurhcaseRequestFilters,
  PurhcaseRequestDetails,
} from "../../../models/PurchaseRequest/PurchaseRequestDetails";
import { PurchaseRequestRejectedBy } from "../../../models/PurchaseRequest/PurchaseRequestBody";
import PurchaseRequestAPI from "../api/PurchaseRequestAPI";
import {
  ShortClosePoLineInfo,
  ShortClosePoLineInput,
} from "../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import { UserStore } from "../../../stores/UserStore";
import OrderAPI from "../api/OrderAPI";
import { APIBlobResponse } from "../../../models/NetworkResponse";
import { PurchaseRequestProduct } from "../../../models/PurchaseRequest/PurchaseRequestProductInsights";
import { VendorsPOReportAPIFilters } from "../../../models/Vendors/VendorsPOReportFilters";
import { VendorsPOReportData } from "../../../models/Vendors/VendorsPOReportData";
import {
  findOrderSkuRemainingQty,
  OrderSkuRemainingQty,
} from "../../../models/PurchaseRequest/PrProductAvailableQty";
import { PurchaseRequestActivityLog } from "../../../models/PurchaseRequest/PurchaseRequestActivityLog";
import { ProductSourcingSkuStatusInfo } from "../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import ProjectPackagingOpsAPI from "../api/ProjectPackagingOpsAPI";
import SwageazyInventoryAPI from "../api/SwageazyInventoryAPI";
import {
  computeSkuInwardedQuantity,
  findSourcingSkuStatus,
} from "../../../utils/purchaseRequestSkuVariants";

const formatAvailableQtyValue = (
  value: number | string | null | undefined,
): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }
  return String(value);
};

export class PurchaseRequestStore {
  purchaseRequestAPI: PurchaseRequestAPI;
  packagingOpsAPI: ProjectPackagingOpsAPI;
  swageazyInventoryAPI: SwageazyInventoryAPI;
  prlist: PurhcaseRequestDetails[];
  limit: number;
  total_pages: number;
  total_count: number;
  purchaseRequestDetail: PurhcaseRequestDetails | null;
  prProductAvailableQty: OrderSkuRemainingQty[];
  editDialog: {
    open: boolean;
    productId: number | null;
    field: string | null;
  };
  cancellationModal: {
    open: boolean;
    poId: number | null;
    reason: string;
  };
  statusChangeModal: {
    open: boolean;
    poId: number | null;
    status: string | null;
  };
  sendingEmails: Set<number>;
  updatingStatus: Set<number>;
  orderAPI: OrderAPI;
  prFilter: string;
  vendorsPOReportData: VendorsPOReportData[];
  activityLogs: PurchaseRequestActivityLog[];
  activityLogsPageNo: number;
  activityLogsLimit: number;
  activityLogsTotalCount: number;
  activityLogsTotalPages: number;
  activityLogsLoading: boolean;
  sourcingInfoBySku: ProductSourcingSkuStatusInfo[];
  sourcingInfoBySkuError: string;
  isSearchExpanded: boolean;
  isListingDebouncedSearchInitialMount: boolean;

  constructor() {
    this.purchaseRequestAPI = new PurchaseRequestAPI();
    this.packagingOpsAPI = new ProjectPackagingOpsAPI();
    this.swageazyInventoryAPI = new SwageazyInventoryAPI();
    this.prlist = [];
    this.limit = 0;
    this.total_count = 0;
    this.total_pages = 0;
    this.purchaseRequestDetail = null;
    this.prProductAvailableQty = [];
    this.vendorsPOReportData = [];
    this.editDialog = {
      open: false,
      productId: null,
      field: null,
    };
    this.cancellationModal = {
      open: false,
      poId: null,
      reason: "",
    };
    this.statusChangeModal = {
      open: false,
      poId: null,
      status: null,
    };
    this.sendingEmails = new Set();
    this.updatingStatus = new Set();
    this.orderAPI = new OrderAPI();
    this.prFilter = "PENDING";
    this.activityLogs = [];
    this.activityLogsPageNo = 1;
    this.activityLogsLimit = 20;
    this.activityLogsTotalCount = 0;
    this.activityLogsTotalPages = 0;
    this.activityLogsLoading = false;
    this.sourcingInfoBySku = [];
    this.sourcingInfoBySkuError = "";
    this.isSearchExpanded = false;
    this.isListingDebouncedSearchInitialMount = true;

    makeObservable(this, {
      prlist: observable,
      limit: observable,
      total_count: observable,
      total_pages: observable,
      getPurchaseRequestListing: action,
      approveRequest: action,
      rejectRequest: action,
      purchaseRequestDetail: observable,
      getPurchaseRequestDetail: action,
      prProductAvailableQty: observable,
      getPrProductAvailableQty: action,
      editDialog: observable,
      openEditDialog: action,
      closeEditDialog: action,
      updateProductField: action,
      patchProductCatalogField: action,
      updateProductGst: action,
      updateProductRate: action,
      updateSkuGst: action,
      updateSkuRate: action,
      updateSkuQuantity: action,
      removeProduct: action,
      removeSkuVariant: action,
      downloadPurchaseOrder: action,
      markUrgentPaymentDone: action,
      cancellationModal: observable,
      statusChangeModal: observable,
      sendingEmails: observable,
      updatingStatus: observable,
      openCancellationModal: action,
      closeCancellationModal: action,
      setCancellationReason: action,
      openStatusChangeModal: action,
      closeStatusChangeModal: action,
      addSendingEmail: action,
      removeSendingEmail: action,
      addUpdatingStatus: action,
      removeUpdatingStatus: action,
      sendEmailToVendor: action,
      updatePOStatus: action,
      generatePO: action,
      reactivatePO: action,
      shortClosePO: action,
      shortClosePoLines: observable,
      shortClosePoLinesLoading: observable,
      getShortClosePoLines: action,
      cancelPurchaseRequest: action,
      prFilter: observable,
      setPrFilter: action,
      vendorsPOReportData: observable,
      getVendorsPOReportData: action,
      resetSourcingApproval: action,
      resetFinanceApproval: action,
      resetSwageazyApproval: action,
      resetAllApprovals: action,
      activityLogs: observable,
      activityLogsPageNo: observable,
      activityLogsLimit: observable,
      activityLogsTotalCount: observable,
      activityLogsTotalPages: observable,
      activityLogsLoading: observable,
      getPurchaseRequestActivityLogs: action,
      resetActivityLogs: action,
      updateWarehouse: action,
      sourcingInfoBySku: observable,
      sourcingInfoBySkuError: observable,
      getSourcingInfoBySku: action,
      getSwageazyInventoryInwardBySku: action,
      clearSourcingInfoBySku: action,
      isSearchExpanded: observable,
      isListingDebouncedSearchInitialMount: observable,
      toggleSearchExpanded: action,
      prepareListingPage: action,
      completeListingDebouncedSearchInitialMount: action,
    });
  }

  prepareListingPage = () => {
    this.isListingDebouncedSearchInitialMount = true;
  };

  completeListingDebouncedSearchInitialMount = () => {
    this.isListingDebouncedSearchInitialMount = false;
  };

  toggleSearchExpanded = () => {
    this.isSearchExpanded = !this.isSearchExpanded;
  };

  getPurchaseRequestListing = async (filters: PurhcaseRequestFilters) => {
    try {
      const response =
        await this.purchaseRequestAPI.getPurchaseRequestListing(filters);
      this.limit = response.limit;
      this.total_count = response.total_count;
      this.total_pages = response.total_pages;
      this.prlist = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  exportPurchaseRequests = async (
    filters: PurhcaseRequestFilters,
  ): Promise<{ success?: boolean; message?: string }> => {
    const response = await this.purchaseRequestAPI.getPurchaseRequestListing(
      filters,
      true,
    );
    return response as unknown as { success?: boolean; message?: string };
  };

  updateWarehouse = async (
    prId: number,
    payload: {
      warehouse_id: string;
      warehouse_address: {
        address_line_1: string | null;
        address_line_2: string | null;
        landmark: string | null;
        city: string | null;
        state: string | null;
        pincode: string | null;
        country_code: string | null;
        contact_name: string | null;
        contact_phone: string | null;
        gstin: string | null;
      };
    },
  ) => {
    const response = await this.purchaseRequestAPI.updatePurchaseRequestWarehouse(
      prId,
      payload,
    );
    if (this.purchaseRequestDetail?.id === prId) {
      this.purchaseRequestDetail = {
        ...this.purchaseRequestDetail,
        warehouse_id: payload.warehouse_id,
        warehouse_identifier: payload.warehouse_id,
        warehouse_address: payload.warehouse_address,
      };
    }
    return response;
  };

  approveRequest = async (
    prId: number,
    team: PurchaseRequestRejectedBy,
    userStore: UserStore,
  ) => {
    try {
      const response = await this.purchaseRequestAPI.approvePurchaseRequest(
        prId,
        team,
      );
      if (response.success) {
        if (team === PurchaseRequestRejectedBy.FINANCE) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                  ...pr,
                  finance_approval: 1,
                  finance_approval_date: new Date().toISOString(),
                  approved_by_finance_admin_id: userStore.user?.id || null,
                  approved_by_finance_adminname: userStore.user?.name || null,
                }
              : pr,
          );
        } else if (team === PurchaseRequestRejectedBy.SOURCING) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                  ...pr,
                  sourcing_approval: 1,
                  sourcing_approval_date: new Date().toISOString(),
                  approved_by_sourcing_admin_id: userStore.user?.id || null,
                  approved_by_sourcing_adminname: userStore.user?.name || null,
                }
              : pr,
          );
        } else if (team === PurchaseRequestRejectedBy.SWAGEAZY) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                  ...pr,
                  swageazy_approval: 1,
                  swageazy_approval_date: new Date().toISOString(),
                  approved_by_swageazy_admin_id: userStore.user?.id || null,
                  approved_by_swageazy_adminname: userStore.user?.name || null,
                }
              : pr,
          );
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  rejectRequest = async (
    prId: number,
    team: PurchaseRequestRejectedBy,
    userStore: UserStore,
    rejectionReason?: string,
  ) => {
    try {
      const response = await this.purchaseRequestAPI.rejectPurchaseRequest(
        prId,
        team,
        rejectionReason,
      );
      if (response.success) {
        if (team === PurchaseRequestRejectedBy.FINANCE) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                  ...pr,
                  finance_approval: 0,
                  rejected_by_finance_admin_id: userStore.user?.id || null,
                  rejected_by_finance_adminname: userStore.user?.name || null,
                  rejection_date_by_finance: new Date().toISOString(),
                }
              : pr,
          );
        } else if (team === PurchaseRequestRejectedBy.SOURCING) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                  ...pr,
                  sourcing_approval: 0,
                  rejected_by_sourcing_admin_id: userStore.user?.id || null,
                  rejected_by_sourcing_adminname: userStore.user?.name || null,
                  rejection_date_by_sourcing: new Date().toISOString(),
                }
              : pr,
          );
        } else if (team === PurchaseRequestRejectedBy.SWAGEAZY) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                  ...pr,
                  swageazy_approval: 0,
                  rejected_by_swageazy_admin_id: userStore.user?.id || null,
                  rejected_by_swageazy_adminname: userStore.user?.name || null,
                  rejection_date_by_swageazy: new Date().toISOString(),
                }
              : pr,
          );
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  generatePO = async (prId: number) => {
    try {
      const response = await this.purchaseRequestAPI.generatePO(prId);
      if (response.success) {
        this.prlist = this.prlist.map((pr) =>
          pr.id === prId
            ? { ...pr, po_generated: 1, po_id: response.result, active_po: 1 }
            : pr,
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getPurchaseRequestDetail = async (prId: number) => {
    try {
      const response =
        await this.purchaseRequestAPI.getPurchaseRequestDetail(prId);
      const listRow = this.prlist.find((pr) => pr.id === prId);
      runInAction(() => {
        this.purchaseRequestDetail = {
          ...response,
          urgent_payment_done:
            response.urgent_payment_done ?? listRow?.urgent_payment_done ?? null,
          urgent_payment_marked_by_admin_id:
            response.urgent_payment_marked_by_admin_id ??
            listRow?.urgent_payment_marked_by_admin_id ??
            null,
          urgent_payment_marked_by_adminname:
            response.urgent_payment_marked_by_adminname ??
            listRow?.urgent_payment_marked_by_adminname ??
            null,
          urgent_payment_marked_at:
            response.urgent_payment_marked_at ??
            listRow?.urgent_payment_marked_at ??
            null,
        };
      });
    } catch (error: any) {
      throw error;
    }
  };

  getPurchaseRequestDetailByPOId = async (poId: number) => {
    try {
      const response =
        await this.purchaseRequestAPI.getPurchaseRequestDetailByPOId(poId);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  openEditDialog = (productId: number, field: string) => {
    this.editDialog = {
      open: true,
      productId,
      field,
    };
  };

  closeEditDialog = () => {
    this.editDialog = {
      open: false,
      productId: null,
      field: null,
    };
  };

  openCancellationModal = (poId: number) => {
    this.cancellationModal = {
      open: true,
      poId,
      reason: "",
    };
  };

  closeCancellationModal = () => {
    this.cancellationModal = {
      open: false,
      poId: null,
      reason: "",
    };
  };

  setCancellationReason = (reason: string) => {
    this.cancellationModal.reason = reason;
  };

  openStatusChangeModal = (poId: number, status: string) => {
    this.statusChangeModal = {
      open: true,
      poId,
      status,
    };
  };

  closeStatusChangeModal = () => {
    this.statusChangeModal = {
      open: false,
      poId: null,
      status: null,
    };
  };

  addSendingEmail = (poId: number) => {
    this.sendingEmails.add(poId);
  };

  removeSendingEmail = (poId: number) => {
    this.sendingEmails.delete(poId);
  };

  addUpdatingStatus = (poId: number) => {
    this.updatingStatus.add(poId);
  };

  removeUpdatingStatus = (poId: number) => {
    this.updatingStatus.delete(poId);
  };

  updateProductField = async (
    product: PurchaseRequestProduct,
    value: string | number,
    field: string,
    projectId?: number,
  ) => {
    try {
      if (!product || !field || !this.purchaseRequestDetail || !projectId) {
        throw new Error("Missing required parameters");
      }

      let response;
      let updatedValue = value;

      // Handle different field types
      if (field === "rate") {
        const to_update_product = {
          ...product,
          rate: Number(value),
        };

        response =
          await this.purchaseRequestAPI.updatePurchaseRequestProduct(
            to_update_product,
          );
        updatedValue = Number(value);
      } else if (field === "gst_percentage") {
        const to_update_product = {
          ...product,
          gst_percentage: Number(value),
        };

        response =
          await this.purchaseRequestAPI.updatePurchaseRequestProduct(
            to_update_product,
          );
        updatedValue = Number(value);
      } else if (field === "hsn_code") {
        // Update product HSN code
        response = await this.orderAPI.updateProductHsn(
          projectId,
          product.product_id,
          String(value),
          product.pr_id,
        );
        updatedValue = String(value);
      } else if (field === "quantity") {
        // Update purchase request product quantity
        const to_update_product = {
          ...product,
          quantity: Number(value),
        };

        response =
          await this.purchaseRequestAPI.updatePurchaseRequestProduct(
            to_update_product,
          );
        updatedValue = Number(value);
      } else {
        throw new Error(`Unsupported field: ${field}`);
      }

      // Only update local state if API call was successful
      if (response && (response as any).success !== false) {
        const updatedProducts = this.purchaseRequestDetail.products?.map(
          (pr) =>
            pr.product_id === product.product_id
              ? { ...pr, [field]: updatedValue }
              : pr,
        );

        this.purchaseRequestDetail = {
          ...this.purchaseRequestDetail,
          products: updatedProducts,
        };
      } else {
        throw new Error("API call failed");
      }

      this.closeEditDialog();
      return response;
    } catch (error) {
      this.closeEditDialog();
      throw error;
    }
  };

  /**
   * Locally sync catalog product fields (e.g. hsn_code) onto the PR detail
   * after a successful catalog sourcing update — no dialog / PR product API.
   */
  patchProductCatalogField = (
    productId: number,
    field: "hsn_code",
    value: string | null,
  ) => {
    if (!this.purchaseRequestDetail) return;

    runInAction(() => {
      const detail = this.purchaseRequestDetail!;
      this.purchaseRequestDetail = {
        ...detail,
        products: (detail.products || []).map((product) =>
          product.product_id === productId
            ? { ...product, [field]: value }
            : product,
        ),
        sku_variants: (detail.sku_variants || []).map((variant) =>
          variant.product_id === productId
            ? { ...variant, [field]: value }
            : variant,
        ),
      };
    });
  };

  private patchProductNumericField = (
    productId: number,
    field: "rate" | "gst_percentage",
    value: number,
  ) => {
    if (!this.purchaseRequestDetail) return;

    runInAction(() => {
      const detail = this.purchaseRequestDetail!;
      this.purchaseRequestDetail = {
        ...detail,
        products: (detail.products || []).map((product) =>
          product.product_id === productId
            ? { ...product, [field]: value }
            : product,
        ),
        sku_variants: (detail.sku_variants || []).map((variant) =>
          variant.product_id === productId
            ? { ...variant, [field]: value }
            : variant,
        ),
      };
    });
  };

  private patchSkuNumericField = (
    sku: string,
    field: "rate" | "gst_percentage",
    value: number,
  ) => {
    if (!this.purchaseRequestDetail) return;
    const normalizedSku = sku.trim().toLowerCase();

    runInAction(() => {
      const detail = this.purchaseRequestDetail!;
      this.purchaseRequestDetail = {
        ...detail,
        sku_variants: (detail.sku_variants || []).map((variant) =>
          (variant.sku || "").trim().toLowerCase() === normalizedSku
            ? { ...variant, [field]: value }
            : variant,
        ),
      };
    });
  };

  updateProductGst = async (
    prId: number,
    productId: number,
    gstPercentage: number,
  ) => {
    try {
      const result = await this.purchaseRequestAPI.updatePurchaseRequestProductGst(
        prId,
        productId,
        { gst_percentage: gstPercentage },
      );
      this.patchProductNumericField(
        productId,
        "gst_percentage",
        result.gst_percentage,
      );
      return result;
    } catch (error) {
      throw error;
    }
  };

  updateProductRate = async (prId: number, productId: number, rate: number) => {
    try {
      const result = await this.purchaseRequestAPI.updatePurchaseRequestProductRate(
        prId,
        productId,
        { rate },
      );
      this.patchProductNumericField(productId, "rate", result.rate);
      return result;
    } catch (error) {
      throw error;
    }
  };

  updateSkuGst = async (prId: number, sku: string, gstPercentage: number) => {
    try {
      const result = await this.purchaseRequestAPI.updatePurchaseRequestSkuGst(
        prId,
        sku,
        { gst_percentage: gstPercentage },
      );
      this.patchSkuNumericField(result.sku, "gst_percentage", result.gst_percentage);
      return result;
    } catch (error) {
      throw error;
    }
  };

  updateSkuRate = async (prId: number, sku: string, rate: number) => {
    try {
      const result = await this.purchaseRequestAPI.updatePurchaseRequestSkuRate(
        prId,
        sku,
        { rate },
      );
      this.patchSkuNumericField(result.sku, "rate", result.rate);
      return result;
    } catch (error) {
      throw error;
    }
  };

  updateSkuQuantity = async (prId: number, sku: string, quantity: number) => {
    try {
      const result =
        await this.purchaseRequestAPI.updatePurchaseRequestSkuQuantity(
          prId,
          sku,
          { quantity },
        );

      runInAction(() => {
        if (!this.purchaseRequestDetail) return;
        const detail = this.purchaseRequestDetail;
        const normalizedSku = result.sku.trim().toLowerCase();

        this.purchaseRequestDetail = {
          ...detail,
          sku_variants: (detail.sku_variants || []).map((variant) =>
            (variant.sku || "").trim().toLowerCase() === normalizedSku
              ? { ...variant, quantity: result.quantity }
              : variant,
          ),
          products: (detail.products || []).map((product) =>
            product.product_id === result.product_id
              ? { ...product, quantity: result.parent_quantity_after }
              : product,
          ),
        };
      });

      return result;
    } catch (error) {
      throw error;
    }
  };

  removeProduct = async (prId: number, productId: number) => {
    try {
      const result =
        await this.purchaseRequestAPI.removePurchaseRequestProduct(
          prId,
          productId,
        );

      runInAction(() => {
        if (!this.purchaseRequestDetail) return;
        const detail = this.purchaseRequestDetail;

        this.purchaseRequestDetail = {
          ...detail,
          products: (detail.products || []).filter(
            (product) => product.product_id !== productId,
          ),
          sku_variants: (detail.sku_variants || []).filter(
            (variant) => variant.product_id !== productId,
          ),
        };
      });

      return result;
    } catch (error) {
      throw error;
    }
  };

  removeSkuVariant = async (prId: number, sku: string) => {
    try {
      const result =
        await this.purchaseRequestAPI.removePurchaseRequestSkuVariant(
          prId,
          sku,
        );

      runInAction(() => {
        if (!this.purchaseRequestDetail) return;
        const detail = this.purchaseRequestDetail;
        const normalizedSku = result.sku.trim().toLowerCase();
        const remainingVariants = (detail.sku_variants || []).filter(
          (variant) =>
            (variant.sku || "").trim().toLowerCase() !== normalizedSku,
        );

        if (result.parent_product_removed) {
          this.purchaseRequestDetail = {
            ...detail,
            products: (detail.products || []).filter(
              (product) => product.product_id !== result.product_id,
            ),
            sku_variants: remainingVariants.filter(
              (variant) => variant.product_id !== result.product_id,
            ),
          };
          return;
        }

        this.purchaseRequestDetail = {
          ...detail,
          sku_variants: remainingVariants,
          products: (detail.products || []).map((product) =>
            product.product_id === result.product_id &&
            result.parent_quantity_after != null
              ? { ...product, quantity: result.parent_quantity_after }
              : product,
          ),
        };
      });

      return result;
    } catch (error) {
      throw error;
    }
  };

  getPrProductAvailableQty = async (productIds: number[], orderId: number) => {
    try {
      // Inventory PRs all use order id -1 and each PR is its own order.
      // There is no shared order balance to calculate.
      if (orderId === -1) {
        runInAction(() => {
          this.prProductAvailableQty = [];
        });
        return;
      }
      const response = await this.purchaseRequestAPI.getPrProductAvailableQty(
        productIds,
        orderId,
      );
      runInAction(() => {
        this.prProductAvailableQty = response ?? [];
      });
    } catch (error) {
      runInAction(() => {
        this.prProductAvailableQty = [];
      });
      throw error;
    }
  };

  getProductBalanceQty = (productId: number): string | null => {
    const skuRows = this.prProductAvailableQty.filter(
      (row) => row.product_id === productId && Boolean(row.sku?.trim()),
    );
    if (skuRows.length === 0) {
      return null;
    }

    const remaining = skuRows.reduce(
      (sum, row) => sum + (Number(row.remaining_quantity) || 0),
      0,
    );
    return formatAvailableQtyValue(remaining);
  };

  clearSourcingInfoBySku = () => {
    this.sourcingInfoBySku = [];
    this.sourcingInfoBySkuError = "";
  };

  getSourcingInfoBySku = async (projectId: number) => {
    try {
      if (!projectId) {
        this.clearSourcingInfoBySku();
        return;
      }

      this.sourcingInfoBySkuError = "";
      const response = await this.packagingOpsAPI.getSourcingInfoBySku(
        projectId,
      );
      this.applySourcingInfoBySku(response);
    } catch (error) {
      this.applySourcingInfoBySkuError();
      throw error;
    }
  };

  getSwageazyInventoryInwardBySku = async (prId: number) => {
    try {
      if (!prId || prId <= 0) {
        this.clearSourcingInfoBySku();
        return;
      }

      this.sourcingInfoBySkuError = "";
      const response =
        await this.swageazyInventoryAPI.getPurchaseRequestInwardBySku(prId);
      this.applySourcingInfoBySku(response);
    } catch (error) {
      this.applySourcingInfoBySkuError();
      throw error;
    }
  };

  private applySourcingInfoBySku = (
    rows: ProductSourcingSkuStatusInfo[] | null | undefined,
  ) => {
    runInAction(() => {
      this.sourcingInfoBySku = rows ?? [];
      this.sourcingInfoBySkuError = "";
    });
  };

  private applySourcingInfoBySkuError = () => {
    runInAction(() => {
      this.sourcingInfoBySku = [];
      this.sourcingInfoBySkuError =
        "Failed to load inwarded quantities for SKUs.";
    });
  };

  getSkuInwardedQty = (
    productId: number,
    variant?: { id?: number | null; sku?: string | null } | null,
  ): number | null => {
    const match = findSourcingSkuStatus(
      this.sourcingInfoBySku,
      productId,
      variant,
    );
    if (!match) {
      return null;
    }

    return computeSkuInwardedQuantity(match);
  };

  getSkuBalanceQty = (
    productId: number,
    variant?: { id?: number | null; sku?: string | null } | null,
  ): string | null => {
    const match = findOrderSkuRemainingQty(
      this.prProductAvailableQty,
      productId,
      variant,
    );

    if (
      !match ||
      match.remaining_quantity === null ||
      match.remaining_quantity === undefined
    ) {
      return null;
    }

    return formatAvailableQtyValue(match.remaining_quantity);
  };


  markUrgentPaymentDone = async (pr_id: number) => {
    await this.purchaseRequestAPI.markUrgentPaymentDone(pr_id);
    await this.getPurchaseRequestDetail(pr_id);
    runInAction(() => {
      const detail = this.purchaseRequestDetail;
      if (detail?.id === pr_id) {
        this.prlist = this.prlist.map((pr) =>
          pr.id === pr_id
            ? {
                ...pr,
                urgent_payment_done: detail.urgent_payment_done,
                urgent_payment_marked_by_admin_id:
                  detail.urgent_payment_marked_by_admin_id,
                urgent_payment_marked_by_adminname:
                  detail.urgent_payment_marked_by_adminname,
                urgent_payment_marked_at: detail.urgent_payment_marked_at,
              }
            : pr,
        );
      }
    });
  };

  downloadPurchaseOrder = async (po_id: number) => {
    try {
      const response =
        await this.purchaseRequestAPI.downloadPurchaseOrder(po_id);
      return response as APIBlobResponse;
    } catch (error: any) {
      throw error;
    }
  };

  sendEmailToVendor = async (pr_id: number, po_id: number) => {
    this.addSendingEmail(po_id);
    try {
      const response = await this.purchaseRequestAPI.sendEmailToVendor(
        pr_id,
        po_id,
      );
      if (response.success && this.purchaseRequestDetail) {
        this.purchaseRequestDetail.purchase_orders =
          this.purchaseRequestDetail.purchase_orders?.map((po) =>
            po.id === po_id
              ? {
                  ...po,
                  email_sent: true,
                  email_sent_date: new Date().toISOString(),
                }
              : po,
          ) || [];
      }
      return response;
    } catch (error: any) {
      throw error;
    } finally {
      this.removeSendingEmail(po_id);
    }
  };

  updatePOStatus = async (
    po_id: number,
    status: string,
    cancellationReason?: string,
  ) => {
    this.addUpdatingStatus(po_id);
    try {
      const response = await this.purchaseRequestAPI.updatePOStatus(
        po_id,
        status,
        cancellationReason,
      );
      if (response.success && this.purchaseRequestDetail) {
        const prId = this.purchaseRequestDetail.id;
        const updatedOrders =
          this.purchaseRequestDetail.purchase_orders?.map((po) => {
            if (po.id !== po_id) return po;
            if (status === "CANCELLED") {
              return {
                ...po,
                status,
                cancellation_reason: cancellationReason,
              };
            }
            if (status === "CREATED" || status === "OPEN") {
              return {
                ...po,
                status,
                cancellation_reason: undefined,
                cancelled_by_admin_id: undefined,
                cancelled_by_adminname: undefined,
              };
            }
            return { ...po, status };
          }) || [];

        const hasActivePo = updatedOrders.some(
          (po) => po.status === "CREATED" || po.status === "OPEN",
        );

        this.purchaseRequestDetail = {
          ...this.purchaseRequestDetail,
          purchase_orders: updatedOrders,
          active_po: hasActivePo ? 1 : 0,
          po_id: this.purchaseRequestDetail.po_id || po_id,
        };

        this.prlist = this.prlist.map((pr) =>
          pr.id === prId ? { ...pr, active_po: hasActivePo ? 1 : 0 } : pr,
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    } finally {
      this.removeUpdatingStatus(po_id);
    }
  };

  cancelPurchaseRequest = async (
    pr_id: number,
    userStore: UserStore,
    cancellationReason?: string,
  ) => {
    try {
      const response = await this.purchaseRequestAPI.cancelPurchaseRequest(
        pr_id,
        cancellationReason,
      );
      if (response.success) {
        this.prlist = this.prlist.map((pr) =>
          pr.id === pr_id
            ? {
                ...pr,
                pr_cancellation_reason: cancellationReason || null,
                active: 0,
                pr_cancellation_date: new Date().toISOString(),
                pr_cancelled_by_admin_id: userStore.user?.id || null,
                pr_cancelled_by_admin_name: userStore.user?.name || null,
              }
            : pr,
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  setPrFilter = (filter: string) => {
    runInAction(() => {
      this.prFilter = filter;
    });
  };

  getVendorsPOReportData = async (filters: VendorsPOReportAPIFilters) => {
    try {
      const response =
        await this.purchaseRequestAPI.getVendorsPOReportData(filters);
      this.vendorsPOReportData = response.result;
    } catch (error: any) {
      throw error;
    }
  };

  reactivatePO = async (prId: number, poId: number) => {
    try {
      const response = await this.purchaseRequestAPI.reactivatePO(prId, poId);
      if (response.success) {
        if (this.purchaseRequestDetail?.id === prId) {
          this.purchaseRequestDetail = {
            ...this.purchaseRequestDetail,
            po_id: poId,
            active_po: 1,
            purchase_orders:
              this.purchaseRequestDetail.purchase_orders?.map((po) =>
                po.id === poId
                  ? {
                      ...po,
                      status: "CREATED",
                      cancellation_reason: undefined,
                      cancelled_by_admin_id: undefined,
                      cancelled_by_adminname: undefined,
                    }
                  : po,
              ) || [],
          };
        }
        // used for listing page
        this.prlist = this.prlist.map((pr) =>
          pr.id === prId ? { ...pr, active_po: 1, po_id: poId } : pr,
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  shortClosePoLines: ShortClosePoLineInfo[] = [];
  shortClosePoLinesLoading = false;

  getShortClosePoLines = async (prId: number, poId: number) => {
    runInAction(() => {
      this.shortClosePoLinesLoading = true;
    });
    try {
      const response = await this.purchaseRequestAPI.getShortClosePoLines(
        prId,
        poId,
      );
      runInAction(() => {
        this.shortClosePoLines = response?.result?.lines ?? [];
      });
      return this.shortClosePoLines;
    } catch (error: any) {
      runInAction(() => {
        this.shortClosePoLines = [];
      });
      throw error;
    } finally {
      runInAction(() => {
        this.shortClosePoLinesLoading = false;
      });
    }
  };

  shortClosePO = async (
    prId: number,
    poId: number,
    lines: ShortClosePoLineInput[],
    reason: string,
  ) => {
    try {
      const response = await this.purchaseRequestAPI.shortClosePO(prId, {
        po_id: poId,
        lines,
        reason,
      });
      if (response.success) {
        await Promise.all([
          this.getPurchaseRequestDetail(prId),
          this.getShortClosePoLines(prId, poId),
        ]);
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  resetSourcingApproval = async (prId: number) => {
    try {
      return await this.purchaseRequestAPI.resetSourcingApproval(prId);
    } catch (error: any) {
      throw error;
    }
  };

  resetFinanceApproval = async (prId: number) => {
    try {
      return await this.purchaseRequestAPI.resetFinanceApproval(prId);
    } catch (error: any) {
      throw error;
    }
  };

  resetSwageazyApproval = async (prId: number) => {
    try {
      return await this.purchaseRequestAPI.resetSwageazyApproval(prId);
    } catch (error: any) {
      throw error;
    }
  };

  resetAllApprovals = async (prId: number) => {
    try {
      return await this.purchaseRequestAPI.resetAllApprovals(prId);
    } catch (error: any) {
      throw error;
    }
  };

  getPurchaseRequestActivityLogs = async (
    prId: number,
    pageNo: number = 1,
    limit: number = this.activityLogsLimit,
  ) => {
    this.activityLogsLoading = true;
    try {
      const response = await this.purchaseRequestAPI.getPurchaseRequestActivityLogs(
        prId,
        { page_no: pageNo, limit },
      );
      runInAction(() => {
        this.activityLogs = response.list || [];
        this.activityLogsPageNo = response.page_no;
        this.activityLogsLimit = response.limit;
        this.activityLogsTotalCount = response.total_count;
        this.activityLogsTotalPages = response.total_pages;
      });
      return response;
    } catch (error: any) {
      throw error;
    } finally {
      runInAction(() => {
        this.activityLogsLoading = false;
      });
    }
  };

  resetActivityLogs = () => {
    this.activityLogs = [];
    this.activityLogsPageNo = 1;
    this.activityLogsLimit = 20;
    this.activityLogsTotalCount = 0;
    this.activityLogsTotalPages = 0;
    this.activityLogsLoading = false;
  };
}
